import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { fetchAllCompaniesBasic } from '../lib/employeeSearch';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { toast } from 'sonner';
import { 
  Plus, Pencil, Trash2, Truck, Wrench, Check, X, Clock, AlertTriangle, 
  Calendar, Loader2, Search, MinusCircle, TimerOff, FileText, Eye, Paperclip
} from 'lucide-react';
import { TablePagination } from '../components/TablePagination';
import { DocumentViewerDialog } from '../components/employees/DocumentViewerDialog';
import { DocumentUploadButton } from '../components/employees/DocumentUploadButton';
import { EquipmentDocPanel } from '../components/documents/EquipmentDocPanel';
import { confirmDialog } from '../components/ConfirmHost';
import { useScreenReady } from '../lib/perfScreen';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Ana tür etiketleri (sabit)
const MAIN_TYPE_LABELS = {
  arac: 'Araç',
  is_ekipmani: 'İş Ekipmanı'
};

// Ekipman türüne göre belgeler (sabit - WorkPermitWizard ile aynı)
const getEquipmentDocs = (equipmentType) => {
  // A grubu (araç/iş makinesi): periyodik muayene + mali mesuliyet; B grubu: periyodik muayene + opsiyonel bakım belgesi
  if (equipmentType === 'arac') {
    return [
      { key: 'periyodik_muayene', label: 'Periyodik Muayene', hasDate: true, dateLabel: 'Geçerlilik Tarihi' },
      { key: 'mali_mesuliyet_sigortasi', label: 'Mali Mesuliyet Sigortası', hasDate: true, dateLabel: 'Geçerlilik Tarihi' },
    ];
  }
  return [
    { key: 'periyodik_muayene', label: 'Periyodik Muayene', hasDate: true, dateLabel: 'Geçerlilik Tarihi' },
    { key: 'bakim_belgesi', label: 'Bakım Belgesi (Opsiyonel)', hasDate: false, optional: true },
  ];
};

// Belge key listesini al
const getEquipmentDocKeys = (equipmentType) => {
  return getEquipmentDocs(equipmentType).map(d => d.key);
};

// Belge süresi dolmuş kontrolü
const isDocumentExpired = (documents, docKey) => {
  const validityKey = docKey + '_validity';
  const dateKey = docKey + '_date';
  const expiryDate = documents && (documents[validityKey] || documents[dateKey]);
  if (!expiryDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expDate = new Date(expiryDate);
  return expDate < today;
};

// Gerçek statüyü al
const getActualDocStatus = (documents, docKey) => {
  const status = documents && documents[docKey] ? documents[docKey] : 'beklemede';
  if (status === 'onay' && isDocumentExpired(documents, docKey)) {
    return 'suresi_doldu';
  }
  return status;
};

// Genel statü hesaplama (kapsam_disi hesaba katılmaz)
const calculateOverallStatus = (documents, docKeys) => {
  if (!documents || docKeys.length === 0) return { status: 'beklemede', label: 'Beklemede', color: 'bg-amber-100 text-amber-700' };
  
  let hasEksik = false;
  let hasBeklemede = false;
  let allOnayOrKapsamDisi = true;
  
  for (const key of docKeys) {
    const status = getActualDocStatus(documents, key);
    
    // Kapsam dışı hesaba katılmaz
    if (status === 'kapsam_disi') continue;
    
    if (status === 'suresi_doldu' || status === 'eksik') {
      hasEksik = true;
      allOnayOrKapsamDisi = false;
    } else if (status === 'beklemede') {
      hasBeklemede = true;
      allOnayOrKapsamDisi = false;
    } else if (status !== 'onay') {
      allOnayOrKapsamDisi = false;
    }
  }
  
  // Eksik > Beklemede > Onay
  if (hasEksik) {
    return { status: 'eksik', label: 'Eksik', color: 'bg-rose-100 text-rose-700' };
  }
  if (hasBeklemede) {
    return { status: 'beklemede', label: 'Beklemede', color: 'bg-amber-100 text-amber-700' };
  }
  if (allOnayOrKapsamDisi) {
    return { status: 'onay', label: 'Onaylı', color: 'bg-emerald-100 text-emerald-700' };
  }
  return { status: 'beklemede', label: 'Beklemede', color: 'bg-amber-100 text-amber-700' };
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const EquipmentPage = () => {
  const { user, hasLocationAccess, canCreate, canEdit, canDelete, isFirm, isReviewer } = useAuth();
  const [equipment, setEquipment] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [operatorList, setOperatorList] = useState([]);
  const [subtypes, setSubtypes] = useState([]); // Dinamik alt türler
  const [templates, setTemplates] = useState([]); // Eksiklik şablonları
  const [loading, setLoading] = useState(true);
  useScreenReady('ekipman_ekrani', !loading);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState('all');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');
  const [selectedLocationFilter, setSelectedLocationFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [typeTotals, setTypeTotals] = useState({ arac: 0, is_ekipmani: 0 });
  const fetchSeq = useRef(0);
  const [openPopover, setOpenPopover] = useState(null);
  const [viewerDoc, setViewerDoc] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  const [formData, setFormData] = useState({
    equipment_type: '',
    equipment_subtype: '',
    location_id: '',
    company_id: '',
    name: '',
    description: '',
    is_active: true,
    documents: {}
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Arama sunucuya 300 ms gecikmeyle gider; değişince 1. sayfaya dönülür
  useEffect(() => {
    const t = setTimeout(() => {
      const next = searchQuery.length >= 3 ? searchQuery : '';
      if (next !== debouncedQuery) { setDebouncedQuery(next); setCurrentPage(1); }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, debouncedQuery]);

  useEffect(() => {
    fetchEquipment();
  }, [selectedTypeFilter, selectedLocationFilter, currentPage, pageSize, debouncedQuery]);

  // Araç / İş Ekipmanı kartları: tüm kayıtlardaki gerçek toplam (lokasyon filtresine göre)
  const loadTypeTotals = () => {
    const loc = selectedLocationFilter !== 'all' ? `&location_id=${selectedLocationFilter}` : '';
    Promise.all(['arac', 'is_ekipmani'].map(t => axios.get(`${API}/equipment?equipment_type=${t}&page_size=1${loc}`).then(r => r.data.total || 0)))
      .then(([arac, is_ekipmani]) => setTypeTotals({ arac, is_ekipmani })).catch(() => {});
  };
  useEffect(loadTypeTotals, [selectedLocationFilter]);
  const refreshList = () => { fetchEquipment(); loadTypeTotals(); };

  const fetchEquipment = async () => {
    const seq = ++fetchSeq.current;
    try {
      let params = `page=${currentPage}&page_size=${pageSize}`;
      if (selectedTypeFilter !== 'all') params += `&equipment_type=${selectedTypeFilter}`;
      if (selectedLocationFilter !== 'all') params += `&location_id=${selectedLocationFilter}`;
      if (debouncedQuery.length >= 3) params += `&q=${encodeURIComponent(debouncedQuery)}`;
      const response = await axios.get(`${API}/equipment?${params}`);
      if (seq !== fetchSeq.current) return; // eski istek yeni sonucun üzerine yazmasın
      const data = response.data;
      setEquipment(data.items || data);
      setTotalItems(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (error) {
      if (seq !== fetchSeq.current) return;
      toast.error('Ekipman yüklenemedi');
    }
  };

  // Sayfa değiştiğinde
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Sayfa boyutu değiştiğinde
  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const fetchData = async () => {
    try {
      const [companiesRes, locationsRes, operatorRes, subtypesRes, templatesRes] = await Promise.all([
        fetchAllCompaniesBasic(API),
        axios.get(API + '/locations'),
        axios.get(API + '/operator-list'),
        axios.get(API + '/equipment-subtypes'),
        axios.get(API + '/deficiency-templates')
      ]);
      // Companies artık pagination formatında geliyor
      setCompanies(companiesRes);
      setLocations(locationsRes.data);
      setOperatorList(operatorRes.data);
      setSubtypes(subtypesRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      toast.error('Veriler yuklenemedi');
    } finally {
      setLoading(false);
    }
  };

  // Eksiklik şablonlarını ekipman türü ve belge türüne göre getir
  const getTemplatesForEquipmentDoc = (equipmentType, subtypeKey, docKey) => {
    const prefix = equipmentType === 'arac' ? 'ekipman_arac_' : 'ekipman_is_';
    let docSuffix = '';
    if (docKey === 'operatorluk_belgesi') docSuffix = '_operatorluk';
    else if (docKey === 'periyodik_muayene') docSuffix = '_periyodik';
    else if (docKey === 'mali_mesuliyet_sigortasi') docSuffix = '_mali';
    
    const fullDocType = `${prefix}${subtypeKey || ''}${docSuffix}`;
    return templates.filter(t => t.document_type === fullDocType);
  };

  // Dinamik alt tür yardımcı fonksiyonları
  const getSubtypesForMainType = (mainType) => subtypes.filter(s => s.main_type === mainType);
  const getSubtypeInfo = (mainType, subtypeKey) => subtypes.find(s => s.main_type === mainType && s.subtype_key === subtypeKey);
  const getSubtypeLabel = (mainType, subtypeKey) => getSubtypeInfo(mainType, subtypeKey)?.subtype_label || subtypeKey;

  const handleOpenDialog = (eq = null) => {
    if (eq) {
      setEditingEquipment(eq);
      setFormData({
        equipment_type: eq.equipment_type,
        equipment_subtype: eq.equipment_subtype,
        location_id: eq.location_id,
        company_id: eq.company_id,
        name: eq.name,
        description: eq.description || '',
        is_active: eq.is_active !== false,
        documents: eq.documents || {}
      });
    } else {
      setEditingEquipment(null);
      setFormData({
        equipment_type: '',
        equipment_subtype: '',
        location_id: '',
        company_id: '',
        name: '',
        description: '',
        is_active: true,
        documents: {}
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData
      };

      if (editingEquipment) {
        await axios.put(API + '/equipment/' + editingEquipment.id, submitData);
        toast.success('Ekipman guncellendi');
        setDialogOpen(false);
      } else {
        await axios.post(API + '/equipment', submitData);
        toast.success('Ekipman kaydedildi');
        setFormData({
          equipment_type: formData.equipment_type,
          equipment_subtype: '',
          location_id: formData.location_id,
          company_id: formData.company_id,
          name: '',
          description: '',
          is_active: true,
          documents: {}
        });
      }
      refreshList();
    } catch (error) {
      const msg = error.response && error.response.data && error.response.data.detail 
        ? error.response.data.detail 
        : 'Islem basarisiz';
      toast.error(msg);
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog({ title: 'Ekipman silinsin mi?', description: 'Bu ekipmanı silmek istediğinizden emin misiniz?', confirmLabel: 'Evet, Sil' }))) return;
    try {
      await axios.delete(API + '/equipment/' + id);
      toast.success('Ekipman silindi');
      refreshList();
    } catch (error) {
      toast.error('Silme islemi basarisiz');
    }
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : '-';
  };

  const getLocationName = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    return location ? location.name : '-';
  };

  // Filtreleme
  const filteredEquipment = equipment.filter(eq => {
    // Yetki kapsamı backend'de uygulanır (lokasyonu tanımsız yetkili ekipman dahil); seçili lokasyon aşağıda
    
    // Tip filtresi
    if (selectedTypeFilter !== 'all' && eq.equipment_type !== selectedTypeFilter) return false;
    
    // Lokasyon filtresi
    if (selectedLocationFilter !== 'all' && eq.location_id !== selectedLocationFilter) return false;
    
    // Durum filtresi
    if (selectedStatusFilter !== 'all') {
      const docKeys = getEquipmentDocKeys(eq.equipment_type);
      const overall = calculateOverallStatus(eq.documents, docKeys);
      if (overall.status !== selectedStatusFilter) return false;
    }
    
    // Ad / firma adı araması sunucuda (tüm kayıtlarda)
    
    return true;
  });

  // Lokasyona göre firmalar
  const filteredCompanies = companies.filter(c => c.location_id === formData.location_id);

  // Kullanıcının erişebildiği lokasyonlar
  const accessibleLocations = locations.filter(loc => 
    user.is_super_admin || hasLocationAccess(loc.id)
  );

  const getDocStatusBadge = (eq, docKey, docLabel) => {
    const status = getActualDocStatus(eq.documents, docKey);
    const date = eq.documents && eq.documents[docKey + '_date'];
    const validity = eq.documents && eq.documents[docKey + '_validity'];
    const note = eq.documents && eq.documents[docKey + '_note'];
    const popoverId = eq.id + '-' + docKey;
    
    let badgeClass = '';
    let statusLabel = '';
    let Icon = Clock;
    
    switch (status) {
      case 'onay':
        badgeClass = 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 cursor-pointer';
        statusLabel = 'Onay';
        Icon = Check;
        break;
      case 'eksik':
        badgeClass = 'bg-rose-100 text-rose-700 hover:bg-rose-200 cursor-pointer';
        statusLabel = 'Eksik';
        Icon = X;
        break;
      case 'suresi_doldu':
        badgeClass = 'bg-orange-100 text-orange-700 hover:bg-orange-200 cursor-pointer';
        statusLabel = 'Süresi Doldu';
        Icon = TimerOff;
        break;
      case 'kapsam_disi':
        badgeClass = 'bg-slate-100 text-slate-500 hover:bg-slate-200 cursor-pointer';
        statusLabel = 'Kapsam Dışı';
        Icon = MinusCircle;
        break;
      default:
        badgeClass = 'bg-amber-100 text-amber-700 hover:bg-amber-200 cursor-pointer';
        statusLabel = 'Beklemede';
        Icon = Clock;
    }

    // Tooltip bilgisi
    const tooltipLines = [docLabel || docKey, statusLabel];
    if (date) tooltipLines.push('Tarih: ' + formatDate(date));
    if (validity) tooltipLines.push('Geçerlilik: ' + formatDate(validity));
    if (note) tooltipLines.push('Not: ' + note);
    
    // Eksiklik nedenleri
    const deficiencies = eq.documents && eq.documents[`${docKey}_deficiencies`];
    if (deficiencies && deficiencies.length > 0) {
      tooltipLines.push('Eksiklikler: ' + deficiencies.join(', '));
    }

    return (
      <Popover open={openPopover === popoverId} onOpenChange={(open) => setOpenPopover(open ? popoverId : null)}>
        <PopoverTrigger asChild>
          <Badge className={badgeClass + ' gap-1 relative'} title={tooltipLines.join('\n')} data-testid={`equipment-doc-badge-${eq.id}-${docKey}`}>
            <Icon className="h-3 w-3" />
            {eq.document_files?.[docKey] && <Paperclip className="h-2.5 w-2.5 absolute -top-1 -right-1 text-blue-600" />}
            <span className="text-xs">{docLabel ? docLabel.split(' ')[0] : docKey}</span>
            {deficiencies && deficiencies.length > 0 && (
              <span className="text-[10px] bg-white/50 px-1 rounded">{deficiencies.length}</span>
            )}
          </Badge>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-4" align="start">
          <div className="space-y-3">
            <h4 className="font-medium text-sm">{docLabel || docKey}</h4>
            
            {/* Durum Seçimi */}
            <div className="space-y-2">
              <Label className="text-xs">Durum</Label>
              <Select
                value={eq.documents && eq.documents[docKey] ? eq.documents[docKey] : 'beklemede'}
                onValueChange={async (value) => {
                  const newDocs = { ...(eq.documents || {}), [docKey]: value };
                  try {
                    // Sadece gerekli alanları gönder
                    const updateData = {
                      company_id: eq.company_id,
                      location_id: eq.location_id,
                      equipment_type: eq.equipment_type,
                      equipment_subtype: eq.equipment_subtype || '',
                      name: eq.name,
                      description: eq.description || '',
                      is_active: eq.is_active !== false,
                      documents: newDocs
                    };
                    await axios.put(API + '/equipment/' + eq.id, updateData);
                    fetchEquipment();
                    toast.success('Belge durumu güncellendi');
                  } catch (error) {
                    console.error('Update error:', error);
                    toast.error('Güncelleme başarısız');
                  }
                }}
                disabled={!canEdit('equipment')}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="beklemede">Beklemede</SelectItem>
                  <SelectItem value="onay" disabled>Onay (önizlemeden)</SelectItem>
                  <SelectItem value="eksik" disabled>Eksik (önizlemeden)</SelectItem>
                  <SelectItem value="kapsam_disi">Kapsam Dışı</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Belge dosyası: onay / red yalnızca önizleme penceresinde */}
            <div className="flex gap-1.5">
              {isFirm && (
                <DocumentUploadButton entityType="equipment" entityId={eq.id} docKey={docKey} hasFile={!!eq.document_files?.[docKey]} onUploaded={() => fetchEquipment()} />
              )}
              {eq.document_files?.[docKey] && (
                <button
                  type="button"
                  onClick={() => { setOpenPopover(null); setViewerDoc({ eq, docKey, label: docLabel || docKey }); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs rounded border ${isReviewer && eq.documents?.[docKey] === 'onay' ? 'border-rose-600 bg-rose-600 text-white hover:bg-rose-700' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
                  data-testid={`equipment-doc-view-btn-${eq.id}-${docKey}`}
                >
                  <Eye className="h-3.5 w-3.5" />{isReviewer ? (eq.documents?.[docKey] === 'onay' ? 'Görüntüle veya Eksik/Hatalı Bildir' : 'Görüntüle ve Onayla') : 'Görüntüle'}
                </button>
              )}
            </div>
            <p className="text-[11px] text-slate-500 bg-slate-50 rounded px-2 py-1.5" data-testid={`equipment-doc-approve-hint-${eq.id}-${docKey}`}>
              {eq.document_files?.[docKey] ? 'Onay / Red için belgeyi görüntüleyin' : 'Onay için önce belge dosyası yükleyin'}
            </p>
            {note && eq.documents?.[docKey] === 'eksik' && <p className="text-xs text-rose-600">Red nedeni: {note}</p>}

            {/* Not alanı */}
            <div className="space-y-2">
              <Label className="text-xs">Not</Label>
              <Input
                className="h-8 text-xs"
                value={eq.documents && eq.documents[docKey + '_note'] ? eq.documents[docKey + '_note'] : ''}
                placeholder="Not ekle..."
                onChange={async (e) => {
                  const newDocs = { ...(eq.documents || {}), [docKey + '_note']: e.target.value };
                  try {
                    const updateData = {
                      company_id: eq.company_id,
                      location_id: eq.location_id,
                      equipment_type: eq.equipment_type,
                      equipment_subtype: eq.equipment_subtype || '',
                      name: eq.name,
                      description: eq.description || '',
                      is_active: eq.is_active !== false,
                      documents: newDocs
                    };
                    await axios.put(API + '/equipment/' + eq.id, updateData);
                    fetchEquipment();
                  } catch (error) {
                    console.error('Update error:', error);
                    toast.error('Güncelleme başarısız');
                  }
                }}
                disabled={!canEdit('equipment')}
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="equipment-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Is Ekipmani</h1>
          <p className="text-slate-500 mt-1">Arac ve is ekipmani yonetimi</p>
        </div>
        {canCreate('equipment') && (
          <Button onClick={() => handleOpenDialog()} className="bg-slate-900 hover:bg-slate-800" data-testid="add-equipment-btn">
            <Plus className="h-4 w-4 mr-2" />
            Ekipman Ekle
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="relative md:col-span-2">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Ekipman veya firma ara (min 3 karakter)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="equipment-search"
          />
        </div>
        <Select value={selectedTypeFilter} onValueChange={(v) => { setSelectedTypeFilter(v); setCurrentPage(1); }}>
          <SelectTrigger data-testid="type-filter">
            <SelectValue placeholder="Tip" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tum Tipler</SelectItem>
            <SelectItem value="arac">Arac</SelectItem>
            <SelectItem value="is_ekipmani">Is Ekipmani</SelectItem>
          </SelectContent>
        </Select>
        <Select value={selectedLocationFilter} onValueChange={(v) => { setSelectedLocationFilter(v); setCurrentPage(1); }}>
          <SelectTrigger data-testid="location-filter">
            <SelectValue placeholder="Lokasyon" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tum Lokasyonlar</SelectItem>
            {accessibleLocations.map(loc => (
              <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={selectedStatusFilter} onValueChange={setSelectedStatusFilter}>
          <SelectTrigger data-testid="status-filter">
            <SelectValue placeholder="Durum" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tum Durumlar</SelectItem>
            <SelectItem value="onay">Onayli</SelectItem>
            <SelectItem value="eksik">Eksik</SelectItem>
            <SelectItem value="beklemede">Beklemede</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Truck className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{typeTotals.arac}</p>
                <p className="text-xs text-slate-500">Arac</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Wrench className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{typeTotals.is_ekipmani}</p>
                <p className="text-xs text-slate-500">Is Ekipmani</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
                <Check className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-900">
                  {equipment.filter(e => {
                    const docKeys = getEquipmentDocKeys(e.equipment_type);
                    return calculateOverallStatus(e.documents, docKeys).status === 'onay';
                  }).length}
                </p>
                <p className="text-xs text-emerald-700">Onaylı</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-rose-200 bg-rose-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-rose-500 flex items-center justify-center">
                <X className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-rose-900">
                  {equipment.filter(e => {
                    const docKeys = getEquipmentDocKeys(e.equipment_type);
                    return calculateOverallStatus(e.documents, docKeys).status === 'eksik';
                  }).length}
                </p>
                <p className="text-xs text-rose-700">Eksik</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Ekipman</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Tür</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Firma</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Lokasyon</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Durum</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Belgeler</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredEquipment.map((eq) => {
                  const equipmentDocs = getEquipmentDocs(eq.equipment_type);
                  const docKeys = equipmentDocs.map(d => d.key);
                  const overall = calculateOverallStatus(eq.documents, docKeys);
                  const isInactive = eq.is_active === false;
                  
                  return (
                    <tr key={eq.id} className={isInactive ? 'bg-slate-50 opacity-60' : 'hover:bg-slate-50'}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {eq.equipment_type === 'arac' ? (
                            <Truck className="h-4 w-4 text-slate-400" />
                          ) : (
                            <Wrench className="h-4 w-4 text-slate-400" />
                          )}
                          <div>
                            <p className="font-medium text-slate-900">{eq.name}</p>
                            {eq.description && <p className="text-xs text-slate-500">{eq.description}</p>}
                          </div>
                          {isInactive && <Badge className="bg-slate-200 text-slate-500 text-xs">Pasif</Badge>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {getSubtypeLabel(eq.equipment_type, eq.equipment_subtype)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{getCompanyName(eq.company_id)}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{getLocationName(eq.location_id)}</td>
                      <td className="px-4 py-3 text-center">
                        <Badge className={overall.color}>{overall.label}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {equipmentDocs.map(doc => (
                            <div key={doc.key} className="flex items-center gap-1">
                              {getDocStatusBadge(eq, doc.key, doc.label)}
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          {canEdit('equipment') && (
                            <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(eq)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {canDelete('equipment') && (
                            <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700" onClick={() => handleDelete(eq.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredEquipment.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-slate-500">
                      Ekipman bulunamadi
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingEquipment ? 'Ekipman Duzenle' : 'Yeni Ekipman'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Ekipman Tipi *</Label>
                <Select
                  value={formData.equipment_type}
                  onValueChange={(value) => setFormData({ ...formData, equipment_type: value, equipment_subtype: '' })}
                  required
                >
                  <SelectTrigger data-testid="equipment-type-select">
                    <SelectValue placeholder="Tip secin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="arac">Arac</SelectItem>
                    <SelectItem value="is_ekipmani">Is Ekipmani</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.equipment_type && (
                <div className="space-y-2">
                  <Label>Alt Tip *</Label>
                  <Select
                    value={formData.equipment_subtype}
                    onValueChange={(value) => setFormData({ ...formData, equipment_subtype: value })}
                    required
                  >
                    <SelectTrigger data-testid="equipment-subtype-select">
                      <SelectValue placeholder="Alt tür seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {getSubtypesForMainType(formData.equipment_type).map(st => (
                        <SelectItem key={st.subtype_key} value={st.subtype_key}>{st.subtype_label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Lokasyon *</Label>
                <Select
                  value={formData.location_id}
                  onValueChange={(value) => setFormData({ ...formData, location_id: value, company_id: '' })}
                  required
                >
                  <SelectTrigger data-testid="equipment-location-select">
                    <SelectValue placeholder="Lokasyon secin" />
                  </SelectTrigger>
                  <SelectContent>
                    {accessibleLocations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.location_id && (
                <div className="space-y-2">
                  <Label>Firma *</Label>
                  <Select
                    value={formData.company_id}
                    onValueChange={(value) => setFormData({ ...formData, company_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="equipment-company-select">
                      <SelectValue placeholder="Firma secin" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredCompanies.map(comp => (
                        <SelectItem key={comp.id} value={comp.id}>{comp.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {filteredCompanies.length === 0 && (
                    <p className="text-xs text-amber-600">Bu lokasyonda kayitli firma yok</p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label>Ekipman Adi / Plaka *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ekipman adi veya plaka"
                  required
                  data-testid="equipment-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Aciklama</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Ek bilgi (istege bagli)"
                  data-testid="equipment-description-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Durum</Label>
                <Select
                  value={formData.is_active ? 'active' : 'passive'}
                  onValueChange={(value) => setFormData({ ...formData, is_active: value === 'active' })}
                >
                  <SelectTrigger data-testid="equipment-status-select">
                    <SelectValue placeholder="Durum secin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                        Aktif
                      </span>
                    </SelectItem>
                    <SelectItem value="passive">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                        Pasif
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Iptal
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-equipment-btn">
                {editingEquipment ? 'Guncelle' : 'Ekle'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DocumentViewerDialog
        open={!!viewerDoc}
        onOpenChange={(open) => !open && setViewerDoc(null)}
        entity={viewerDoc ? (equipment.find(e => e.id === viewerDoc.eq.id) || viewerDoc.eq) : null}
        entityType="equipment"
        subtitle={viewerDoc?.eq.name}
        docKey={viewerDoc?.docKey}
        docLabel={viewerDoc?.label}
        mode="latest"
        onReplaced={isReviewer ? () => fetchEquipment() : undefined}
        decisionPanel={viewerDoc && isReviewer && (
          <EquipmentDocPanel
            key={`${viewerDoc.eq.id}-${viewerDoc.docKey}`}
            equipment={equipment.find(e => e.id === viewerDoc.eq.id) || viewerDoc.eq}
            docKey={viewerDoc.docKey}
            docLabel={viewerDoc.label}
            templates={getTemplatesForEquipmentDoc(viewerDoc.eq.equipment_type, viewerDoc.eq.equipment_subtype, viewerDoc.docKey).map(t => t.template_text)}
            operatorList={operatorList}
            onSaved={() => { setViewerDoc(null); fetchEquipment(); }}
          />
        )}
      />
    </div>
  );
};

export default EquipmentPage;
